import {
  AbstractControl,
  AsyncValidatorFn,
  FormControl,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms'

import { isAfter, isBefore, isEqual, isToday, isValid, parse, parseISO } from 'date-fns'
import { isBoolean } from 'lodash'
import isEmail from 'validator/lib/isEmail'

import { Observable, debounceTime, first, map, of, switchMap } from 'rxjs'

import {
  ComponentTemplateConfig,
  DatepickerTemplateConfig,
  ExpressionLogMeta,
  FlxValidator,
  FormFieldsEnum,
  NavigationAreaTemplateConfig,
  TemplateConfig,
  ValidatorType,
  evalSingleExpression$,
  isValidISO8601,
  processDataValueFromPath$,
  resolveAsyncValidator,
  resolveValidator,
} from '@flowx/core-sdk'

import { expressionLogMeta } from './expression-log-meta'

export type ValidatorFnFactory = (...args: any[]) => ValidatorFn

function isDateString(value?: string): boolean {
  if (!value) {
    return false
  }
  if (
    !/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/.test(value) &&
    !/\d{4}-\d{2}-\d{2}/.test(value)
  ) {
    return false
  }
  return true
}

export type AsyncValidatorFnFactory = (
  args?: Observable<any>[],
  config?: TemplateConfig,
  logMeta?: ExpressionLogMeta
) => AsyncValidatorFn

export type NgSyncValidator = ValidatorFn | ValidatorFnFactory

export type NgAsyncValidator = AsyncValidatorFn | AsyncValidatorFnFactory

const CustomValidators = {
  minDate:
    (value: string, standard: boolean) =>
    (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null
      }

      const minDate = isValidISO8601(value as string)
        ? new Date(value as string)
        : parse(value as string, 'dd.MM.yyyy', new Date())
      const parsedValue = standard
        ? parseISO(control.value)
        : parse(control.value, 'dd.MM.yyyy', new Date())

      if (!isValid(parsedValue) || !isValid(minDate)) {
        return null
      }

      return isEqual(minDate, parsedValue) || isBefore(minDate, parsedValue)
        ? null
        : { minDate: true }
    },
  maxDate:
    (value: string, standard: boolean) =>
    (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null
      }

      const maxDate = isValidISO8601(value as string)
        ? new Date(value as string)
        : parse(value as string, 'dd.MM.yyyy', new Date())
      const parsedValue = standard
        ? parseISO(control.value)
        : parse(control.value, 'dd.MM.yyyy', new Date())

      if (!isValid(parsedValue) || !isValid(maxDate)) {
        return null
      }

      return isEqual(maxDate, parsedValue) || isAfter(maxDate, parsedValue)
        ? null
        : { maxDate: true }
    },
}

export const FLX_VALIDATORS: Record<string, NgSyncValidator> = {
  required: Validators.required,
  requiredTrue: (control: AbstractControl): ValidationErrors | null =>
    control.value === true ? null : { requiredTrue: true },
  email: (control: AbstractControl): ValidationErrors | null =>
    !control.value || isEmail(control.value) ? null : { email: true },
  pattern: Validators.pattern,
  minlength: Validators.minLength,
  maxlength: Validators.maxLength,
  min: Validators.min,
  max: Validators.max,
  isSameOrBeforeToday: (AC: AbstractControl): ValidationErrors | null => {
    const controlValue = AC.value
    if (!controlValue) {
      return null
    }
    let parsedValue: Date
    if (isDateString(controlValue)) {
      parsedValue = parseISO(controlValue as string)
    } else {
      parsedValue = parse(controlValue as string, 'dd.MM.yyyy', new Date())
    }
    return isValid(parsedValue) && (isBefore(parsedValue, new Date()) || isToday(parsedValue))
      ? null
      : { isSameOrBeforeToday: true }
  },
  isSameOrAfterToday: (AC: AbstractControl): ValidationErrors | null => {
    const controlValue = AC.value
    if (!controlValue) {
      return null
    }
    let parsedValue: Date
    if (isDateString(controlValue)) {
      parsedValue = parseISO(controlValue as string)
    } else {
      parsedValue = parse(controlValue as string, 'dd.MM.yyyy', new Date())
    }
    return isValid(parsedValue) && (isAfter(parsedValue, new Date()) || isToday(parsedValue))
      ? null
      : { isSameOrAfterToday: true }
  },
  minDate: CustomValidators.minDate,
  maxDate: CustomValidators.maxDate,
}

export const FLX_ASYNC_VALIDATORS = {
  min(
    [minExpression]: Observable<any>[],
    config: TemplateConfig,
    logMeta?: ExpressionLogMeta
  ): AsyncValidatorFn {
    return expressionValueValidatorFactory(
      minExpression,
      config,
      Validators.min,
      undefined,
      logMeta
    )
  },
  max(
    [maxExpression]: Observable<any>[],
    config: TemplateConfig,
    logMeta?: ExpressionLogMeta
  ): AsyncValidatorFn {
    return expressionValueValidatorFactory(
      maxExpression,
      config,
      Validators.max,
      undefined,
      logMeta
    )
  },
  minlength(
    [minLengthExpression]: Observable<any>[],
    config: TemplateConfig,
    logMeta?: ExpressionLogMeta
  ): AsyncValidatorFn {
    return expressionValueValidatorFactory(
      minLengthExpression,
      config,
      Validators.minLength,
      undefined,
      logMeta
    )
  },
  maxlength(
    [maxLengthExpression]: Observable<any>[],
    config: TemplateConfig,
    logMeta?: ExpressionLogMeta
  ): AsyncValidatorFn {
    return expressionValueValidatorFactory(
      maxLengthExpression,
      config,
      Validators.maxLength,
      undefined,
      logMeta
    )
  },
  minDate(
    [minDateExpression]: Observable<any>[],
    config: DatepickerTemplateConfig,
    logMeta?: ExpressionLogMeta
  ): AsyncValidatorFn {
    const { standard } = config.displayOptions.flowxProps

    return expressionValueValidatorFactory(
      minDateExpression,
      config,
      (value: string) => CustomValidators.minDate(value, standard),
      { parseNumber: false },
      logMeta
    )
  },
  maxDate(
    [maxDateExpression]: Observable<any>[],
    config: DatepickerTemplateConfig,
    logMeta?: ExpressionLogMeta
  ): AsyncValidatorFn {
    const { standard } = config.displayOptions.flowxProps

    return expressionValueValidatorFactory(
      maxDateExpression,
      config,
      (value: string) => CustomValidators.maxDate(value, standard),
      { parseNumber: false },
      logMeta
    )
  },
  customExpression(
    [customExpression]: Observable<any>[],
    config: TemplateConfig,
    logMeta?: ExpressionLogMeta
  ): AsyncValidatorFn {
    return (fc: AbstractControl): Observable<ValidationErrors | null> =>
      customExpression.pipe(
        switchMap((expr) => evalSingleExpression$(expr, config, logMeta)),
        switchMap((result) =>
          of(!fc.value ? null : isBoolean(result) && result ? null : { customExpression: true })
        ),
        debounceTime(0),
        first()
      )
  },
}

function expressionValueValidatorFactory(
  expression: Observable<any>,
  templateConfig: TemplateConfig,
  validatorFn: (value: number | string) => ValidatorFn,
  { parseNumber } = { parseNumber: false },
  logMeta?: ExpressionLogMeta
) {
  return (fc: AbstractControl): Observable<ValidationErrors | null> =>
    expression.pipe(
      switchMap((expr) => evalSingleExpression$(expr, templateConfig, logMeta)),
      switchMap((value) => {
        // we simulate a sync validator, in order to avoid the error handling and message setup
        const testFc = new FormControl(fc.value, {
          validators: [validatorFn(parseNumber ? parseInt(value, 10) : value)],
        })
        return of(testFc.errors)
      }),
      debounceTime(0),
      first() // THIS! IS! SUPER! IMPORTANT!!, because otherwise the root observable (expression) will never complete and form element will be stuck in PENDING state
    )
}

export function buildValidators(
  type: ValidatorType,
  config: ComponentTemplateConfig | NavigationAreaTemplateConfig
): ValidatorFn[] | AsyncValidatorFn[] {
  const { processInstanceUuid } = config
  const validators = 'validators' in config ? config.validators : []

  if (
    config.componentIdentifier === FormFieldsEnum.DATEPICKER &&
    'displayOptions' in config &&
    config.displayOptions.flowxProps
  ) {
    const {
      minDate,
      maxDate,
      matDatepickerMin,
      matDatepickerMax,
      isComputedMinDate,
      isComputedMaxDate,
      standard,
    } = config.displayOptions.flowxProps

    if (minDate) {
      validators.push({
        name: 'minDate',
        type: isComputedMinDate ? ValidatorType.ASYNC : ValidatorType.SYNC,
        params: [minDate, standard],
        message: matDatepickerMin,
        isCustom: false,
      })
    }

    if (maxDate) {
      validators.push({
        name: 'maxDate',
        type: isComputedMaxDate ? ValidatorType.ASYNC : ValidatorType.SYNC,
        params: [maxDate, standard],
        message: matDatepickerMax,
        isCustom: false,
      })
    }
  }

  return validators
    .filter(({ type: validatorType = 'sync' }: FlxValidator) => validatorType === type)
    .map((validator) => {
      const { name, type = 'sync', params } = validator

      switch (type) {
        case ValidatorType.SYNC: {
          return params?.length > 0
            ? (resolveValidator(name) as ValidatorFnFactory)(...params)
            : (resolveValidator(name) as ValidatorFn)
        }
        case ValidatorType.ASYNC: {
          if (!processInstanceUuid) {
            throw new Error(
              `Could not resolve async validator ${name}. Missing process instance UUID.`
            )
          }
          const params$: Observable<any>[] = params.map((param) =>
            processDataValueFromPath$(processInstanceUuid, param).pipe(
              map((value) => value ?? param)
            )
          )
          // params$ resolve each param against live process data, so the
          // authored text only survives here — stamp it for the log stream.
          const logMeta = expressionLogMeta(config, {
            logType: 'validator',
            originalExpression: String(params[0]),
          })
          return params$.length
            ? (resolveAsyncValidator(name) as AsyncValidatorFnFactory)(params$, config, logMeta)
            : (resolveAsyncValidator(name) as AsyncValidatorFn)
        }
        default:
          throw new Error(`Could not resolve validator ${name}`)
      }
    })
    .filter((validator) => Boolean(validator))
}

export function buildFieldValidators(
  config: ComponentTemplateConfig | NavigationAreaTemplateConfig
): ValidatorFn[] {
  return buildValidators(ValidatorType.SYNC, config) as ValidatorFn[]
}

export function buildAsyncValidators(
  config: ComponentTemplateConfig | NavigationAreaTemplateConfig
): AsyncValidatorFn[] {
  return buildValidators(ValidatorType.ASYNC, config) as AsyncValidatorFn[]
}
